import {
  skipKycDocument,
  submitKycDocument,
  uploadKycDocument
} from '@basqet-dashboard/common/api';
import { Button, FormInput } from '@basqet-dashboard/common/components';
import { KYC_INPUT_TYPE } from '@basqet-dashboard/common/config';
import {
  formatDocumentTitle,
  formatErrorMessage,
  isDocumentMandatory,
  validateKycFileFormat
} from '@basqet-dashboard/common/utils';
import PropTypes from 'prop-types';
import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

const KycForm = ({
  title,
  fileToUpload = '',
  fileInfo,
  fileDocument,
  onSkip,
  inputType = KYC_INPUT_TYPE.FILE,
  refetchKycInfo
}) => {
  const documentTitle = formatDocumentTitle(fileToUpload);
  // Backend returns input_type (snake_case), frontend config uses inputType (camelCase)
  const documentInputType =
    fileInfo?.input_type || fileInfo?.inputType || inputType || KYC_INPUT_TYPE.FILE;

  const label = generateLabel(fileInfo, documentTitle, documentInputType);
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting }
  } = useForm({ mode: 'onChange' });

  const fileValue = watch('file');
  const textValue = watch('value');
  const hasFile = fileValue && fileValue.length > 0;
  const hasTextValue = textValue && textValue.trim().length > 0;

  const submitDocument = async (data, e) => {
    e.preventDefault();

    try {
      if (documentInputType === KYC_INPUT_TYPE.FILE) {
        const validation = validateKycFileFormat(data.file);
        if (validation !== true) {
          toast.error(validation);
          return;
        }

        const { file } = data;
        const requestBody = new FormData();
        requestBody.append('file', file[0], file[0].name);
        requestBody.append('document_title', fileToUpload);
        await uploadKycDocument(requestBody);
      } else {
        const value = data.value?.trim();
        if (!value) {
          toast.error(
            documentInputType === KYC_INPUT_TYPE.TEXT
              ? 'Please enter a value'
              : 'Please enter a valid URL'
          );
          return;
        }

        if (documentInputType === KYC_INPUT_TYPE.URL) {
          try {
            new URL(value);
          } catch {
            toast.error('Please enter a valid URL');
            return;
          }
        }

        const requestBody = new FormData();
        requestBody.append('document_title', fileToUpload);
        requestBody.append('value', value);
        requestBody.append('input_type', documentInputType);
        await submitKycDocument(requestBody);
      }

      toast.success(
        `${documentTitle} ${
          documentInputType === KYC_INPUT_TYPE.FILE ? 'uploaded' : 'submitted'
        } successfully!`
      );
      reset();
      await refetchKycInfo();
    } catch (error) {
      formatErrorMessage(error);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    try {
      await skipKycDocument(fileToUpload);
      toast.success('Document skipped successfully');
      await refetchKycInfo();
    } catch (error) {
      formatErrorMessage(error);
    }
  };

  const isOptional = fileDocument ? !isDocumentMandatory(fileDocument) : false;
  const isMandatory = !isOptional;

  const hasRequiredInput = documentInputType === KYC_INPUT_TYPE.FILE ? hasFile : hasTextValue;
  const isNextDisabled = isMandatory && !hasRequiredInput;

  const renderInput = () => {
    if (documentInputType === KYC_INPUT_TYPE.FILE) {
      return (
        <FormInput
          type="file"
          placeholder="Select file"
          id="file"
          name="file"
          label={label}
          control={control}
          register={register('file', {
            validate: (file) => {
              const validation = validateKycFileFormat(file);
              return validation === true || validation;
            }
          })}
          accept=".pdf,.jpg,.jpeg,.png"
        />
      );
    } else if (documentInputType === KYC_INPUT_TYPE.TEXT) {
      return (
        <FormInput
          type="text"
          placeholder="Enter value"
          id="value"
          name="value"
          label={label}
          control={control}
          register={register('value', {
            required: 'This field is required',
            validate: (value) => {
              if (!value || !value.trim()) {
                return 'Please enter a value';
              }
              return true;
            }
          })}
        />
      );
    } else if (documentInputType === KYC_INPUT_TYPE.URL) {
      return (
        <FormInput
          type="url"
          placeholder="https://example.com"
          id="value"
          name="value"
          label={label}
          control={control}
          register={register('value', {
            required: 'URL is required',
            validate: (value) => {
              if (!value || !value.trim()) {
                return 'Please enter a URL';
              }
              try {
                new URL(value);
                return true;
              } catch {
                return 'Please enter a valid URL';
              }
            }
          })}
        />
      );
    }
    return null;
  };

  return (
    <div>
      <div className="card form__container">
        <h3 className="card__title">
          Activate your <span className="text-cursive">{title} account</span>
        </h3>
        <form className="card__body" onSubmit={handleSubmit(submitDocument)}>
          {renderInput()}
          <div className="kyc-form__buttons">
            <Button text="Next" isLoading={isSubmitting} disabled={isNextDisabled}>
              Next
            </Button>
            {isOptional && (
              <Button type="button" variant="outline" onClick={handleSkip} disabled={isSubmitting}>
                Skip
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const generateLabel = (fileInfo, documentTitle, inputType) => {
  let label = documentTitle;
  const { supported_docs, url } = fileInfo || {};

  if (inputType === KYC_INPUT_TYPE.TEXT) {
    label = `Enter your ${documentTitle}`;
  } else if (inputType === KYC_INPUT_TYPE.URL) {
    label = `Enter the URL for ${documentTitle}`;
  } else if (supported_docs) {
    label = (
      <>
        Please note that a valid {documentTitle.toLowerCase()} is the following:
        <ul>
          {supported_docs.map((doc, index) => (
            <li key={`supported-doc-${index}`}>{formatDocumentTitle(doc)}</li>
          ))}
        </ul>
      </>
    );
  } else if (url) {
    label = (
      <>
        Download and fill{' '}
        <a href={url} download="">
          form
        </a>{' '}
        and upload
      </>
    );
  }

  return label;
};

KycForm.propTypes = {
  title: PropTypes.string,
  uploadedFiles: PropTypes.array,
  fileToUpload: PropTypes.string,
  fileInfo: PropTypes.object,
  fileDocument: PropTypes.object,
  onSkip: PropTypes.func,
  inputType: PropTypes.string,
  refetchKycInfo: PropTypes.func
};

export { KycForm };
